import { useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import FormCheckboxField from 'components/IAP/formFields/FormCheckboxField';
import FormRadioField from 'components/IAP/formFields/FormRadioField';
import FormTextAreaField from 'components/IAP/formFields/FormTextAreaField';
import FormTextField from 'components/IAP/formFields/FormTextField';
import {
  ResourceRequest,
  useUpdateResourceRequest,
} from 'components/IAP/hooks/incidentResourceRequestHooks';
import { ICS204_OUTLINE_BTN } from 'components/IAP/planningView/widgets/AssignmentList/assignmentCardStyles';
import { toDatetimeLocalValue } from 'components/IAP/ResourceRequests/ics213rr/Ics213RrFormBody';
import type { Ics213RrEditingSection } from 'components/IAP/ResourceRequests/ics213rr/ics213rrSections';

type SectionEditorProps = {
  request: ResourceRequest;
  onDone: () => void;
  onCancel: () => void;
};

const SectionActions = ({
  isSaving,
  onCancel,
}: {
  isSaving: boolean;
  onCancel: () => void;
}) => (
  <div className="tw:mt-3 tw:flex tw:items-center tw:justify-end tw:gap-2">
    <Button
      type="button"
      size="sm"
      variant="outline"
      className={ICS204_OUTLINE_BTN}
      disabled={isSaving}
      onClick={onCancel}
    >
      Cancel
    </Button>
    <Button type="submit" size="sm" disabled={isSaving}>
      {isSaving ? 'Saving…' : 'Save'}
    </Button>
  </div>
);

function usePatchRequest(request: ResourceRequest, onDone: () => void) {
  const { mutateAsync } = useUpdateResourceRequest();
  const [isSaving, setIsSaving] = useState(false);

  const patch = async (data: Partial<ResourceRequest>) => {
    setIsSaving(true);
    try {
      await mutateAsync({
        requestId: request.resource_request_id,
        data: { ...request, ...data },
      });
      onDone();
    } catch {
      // Mutation hooks toast; keep the form open for retry.
    } finally {
      setIsSaving(false);
    }
  };

  return { patch, isSaving };
}

export function DateTimeSectionEdit({
  request,
  onDone,
  onCancel,
}: SectionEditorProps) {
  const { patch, isSaving } = usePatchRequest(request, onDone);
  const form = useForm({
    defaultValues: {
      timestamp: toDatetimeLocalValue(request.timestamp),
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics213rr-section-edit-dateTime"
        onSubmit={form.handleSubmit(async (values) => {
          const timestamp = values.timestamp
            ? new Date(values.timestamp)
            : request.timestamp;
          await patch({ timestamp });
        })}
      >
        <Input
          type="datetime-local"
          aria-label="Date/Time"
          {...form.register('timestamp')}
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
}

export function RequestNumberSectionEdit({
  request,
  onDone,
  onCancel,
}: SectionEditorProps) {
  const { patch, isSaving } = usePatchRequest(request, onDone);
  const form = useForm({
    defaultValues: {
      request_number: request.request_number ?? '',
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics213rr-section-edit-requestNumber"
        onSubmit={form.handleSubmit(async (values) => {
          await patch({
            request_number:
              values.request_number?.trim() || request.request_number,
          });
        })}
      >
        <Input
          aria-label="Resource Request Number"
          {...form.register('request_number')}
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
}

export function SupplySourcesSectionEdit({
  request,
  onDone,
  onCancel,
}: SectionEditorProps) {
  const { patch, isSaving } = usePatchRequest(request, onDone);
  const form = useForm({
    defaultValues: {
      supply_sources: request.supply_sources ?? '',
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics213rr-section-edit-supplySources"
        onSubmit={form.handleSubmit(async (values) => {
          await patch({ supply_sources: values.supply_sources });
        })}
      >
        <FormTextAreaField fieldName="supply_sources" />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
}

export function ReslCheckboxesSectionEdit({
  request,
  onDone,
  onCancel,
}: SectionEditorProps) {
  const { patch, isSaving } = usePatchRequest(request, onDone);
  const form = useForm({
    defaultValues: {
      resl_tac: !!request.resl_tac,
      resl_personnel: !!request.resl_personnel,
      resl_available: !!request.resl_available,
      resl_not_available: !!request.resl_not_available,
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics213rr-section-edit-reslCheckboxes"
        onSubmit={form.handleSubmit(async (values) => {
          await patch({
            resl_tac: values.resl_tac,
            resl_personnel: values.resl_personnel,
            resl_available: values.resl_available,
            resl_not_available: values.resl_not_available,
          });
        })}
      >
        <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-5">
          <FormCheckboxField title="a. Tactical" fieldName="resl_tac" />
          <FormCheckboxField title="a. Personnel" fieldName="resl_personnel" />
          <FormCheckboxField title="b. Available" fieldName="resl_available" />
          <FormCheckboxField
            title="c. Not available"
            fieldName="resl_not_available"
          />
        </div>
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
}

export function LogisticsOrderSectionEdit({
  request,
  onDone,
  onCancel,
}: SectionEditorProps) {
  const { patch, isSaving } = usePatchRequest(request, onDone);
  const form = useForm({
    defaultValues: {
      logistics_order_number: request.logistics_order_number ?? '',
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics213rr-section-edit-logisticsOrder"
        onSubmit={form.handleSubmit(async (values) => {
          await patch({
            logistics_order_number: values.logistics_order_number,
          });
        })}
      >
        <FormTextField
          title="Requisition / Purchase Order #"
          fieldName="logistics_order_number"
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
}

export function SupplierSectionEdit({
  request,
  onDone,
  onCancel,
}: SectionEditorProps) {
  const { patch, isSaving } = usePatchRequest(request, onDone);
  const form = useForm({
    defaultValues: {
      supplier_name: request.supplier_name ?? '',
      supplier_phone: request.supplier_phone ?? '',
      supplier_fax: request.supplier_fax ?? '',
      supplier_email: request.supplier_email ?? '',
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics213rr-section-edit-supplier"
        onSubmit={form.handleSubmit(async (values) => {
          await patch(values);
        })}
      >
        <div className="tw:grid tw:grid-cols-1 tw:gap-2 tw:sm:grid-cols-2">
          <FormTextField title="Name" fieldName="supplier_name" />
          <FormTextField title="Phone" fieldName="supplier_phone" />
          <FormTextField title="Fax" fieldName="supplier_fax" />
          <FormTextField title="Email" fieldName="supplier_email" />
        </div>
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
}

export function LogisticsNotesSectionEdit({
  request,
  onDone,
  onCancel,
}: SectionEditorProps) {
  const { patch, isSaving } = usePatchRequest(request, onDone);
  const form = useForm({
    defaultValues: {
      logistics_notes: request.logistics_notes ?? '',
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics213rr-section-edit-logisticsNotes"
        onSubmit={form.handleSubmit(async (values) => {
          await patch({ logistics_notes: values.logistics_notes });
        })}
      >
        <FormTextAreaField title="Notes" fieldName="logistics_notes" />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
}

export function OrderPlacedBySectionEdit({
  request,
  onDone,
  onCancel,
}: SectionEditorProps) {
  const { patch, isSaving } = usePatchRequest(request, onDone);
  const form = useForm({
    defaultValues: {
      logistics_order_placed_by: request.logistics_order_placed_by ?? '',
      logistics_order_placed_by_other:
        request.logistics_order_placed_by_other ?? '',
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics213rr-section-edit-orderPlacedBy"
        onSubmit={form.handleSubmit(async (values) => {
          await patch(values);
        })}
      >
        <FormRadioField
          title="Order placed by"
          fieldName="logistics_order_placed_by"
          options={['SPUL', 'PROC', 'Other']}
          className="tw:flex tw:justify-between tw:pe-2"
        />
        <FormTextField
          title="Other"
          fieldName="logistics_order_placed_by_other"
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
}

export function FinanceCommentsSectionEdit({
  request,
  onDone,
  onCancel,
}: SectionEditorProps) {
  const { patch, isSaving } = usePatchRequest(request, onDone);
  const form = useForm({
    defaultValues: {
      finance_comments: request.finance_comments ?? '',
    },
  });

  return (
    <FormProvider {...form}>
      <form
        data-testid="ics213rr-section-edit-financeComments"
        onSubmit={form.handleSubmit(async (values) => {
          await patch({ finance_comments: values.finance_comments });
        })}
      >
        <FormTextAreaField
          title="Reply / Comments from Finance"
          fieldName="finance_comments"
        />
        <SectionActions isSaving={isSaving} onCancel={onCancel} />
      </form>
    </FormProvider>
  );
}

export function renderIcs213RrSectionEditor(
  section: Ics213RrEditingSection,
  props: SectionEditorProps
) {
  switch (section) {
    case 'dateTime':
      return <DateTimeSectionEdit {...props} />;
    case 'requestNumber':
      return <RequestNumberSectionEdit {...props} />;
    case 'supplySources':
      return <SupplySourcesSectionEdit {...props} />;
    case 'reslCheckboxes':
      return <ReslCheckboxesSectionEdit {...props} />;
    case 'logisticsOrder':
      return <LogisticsOrderSectionEdit {...props} />;
    case 'supplier':
      return <SupplierSectionEdit {...props} />;
    case 'logisticsNotes':
      return <LogisticsNotesSectionEdit {...props} />;
    case 'orderPlacedBy':
      return <OrderPlacedBySectionEdit {...props} />;
    case 'financeComments':
      return <FinanceCommentsSectionEdit {...props} />;
    default:
      return null;
  }
}
